import { z } from "zod";
import { Badge, Box, Card, CardActionArea, Skeleton, Typography } from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { Row } from "../../../misc/Row";
import { overviewCardStyle } from "../../../misc/backofficeTheme";
import { schemas, serverAPI } from "../../../api/server.api";
import { useCurrentDrivingSchoolId } from "../../../hooks/useCurrentDrivingSchoolId";
import { useThemeColor } from "../../../hooks/useThemeColor";
import { useStudentLabelPalette } from "../../../hooks/useStudentLabelPalette";
import { useQuery, useQueryClient } from "react-query";
import { DrivingSchoolLabel } from "../../../providers/drivingSchoolsProvider";
import { grants } from "../../../backoffice.access_control";
import { useDialog } from "../../../hooks/useDialog";
import { SignatureFoldersDialog } from "../../../dialogs/SignatureFoldersDialog";

type StatisticsOverviewDto = z.infer<typeof schemas.StatsOverviewDto>;

export function StatisticsOverviewCard() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const queryClient = useQueryClient();
  const { dialogProps, openDialog } = useDialog("signatureFolders");
  const { data: statisticsOverview, isLoading } = useQuery<StatisticsOverviewDto>(
    ["statisticsOverview", drivingSchoolId],
    () => serverAPI.getOverviewStats(drivingSchoolId),
  );

  if (isLoading) {
    return (
      <Box role="status" aria-label="Statistiken werden geladen" sx={overviewCardsContainerStyle}>
        <_OverviewCardSkeleton title={`Anmel\u00ADdungen`} subtitle="Letzte 30 Tage" />
        <_OverviewCardSkeleton title="Aktive Schüler" subtitle="Letzte 30 Tage" />
        <_OverviewCardSkeleton title="Praktische Prüfungen" subtitle="Aktuelle Woche" />
        <_OverviewCardSkeleton title={`Bestehens\u00ADquote`} subtitle="Letzte 30 Tage" />
        <_OverviewCardSkeleton title={`Unter\u00ADschriften\u00ADmappe`} subtitle="Dokumente" />
      </Box>
    );
  }
  if (!statisticsOverview) return null;

  const { activeStudents, practicalPassRate, practicalExams, openSignatures, registrations } = statisticsOverview;

  return (
    <Box sx={overviewCardsContainerStyle}>
      <_OverviewCard
        title={`Anmel\u00ADdungen`}
        subtitle="Letzte 30 Tage"
        value={registrations.currentPeriod}
        delta={registrations.delta}
        trend={registrations.trend}
        icon={<PersonOutlinedIcon fontSize="inherit" />}
      />
      <_OverviewCard
        title="Aktive Schüler"
        subtitle="Letzte 30 Tage"
        value={activeStudents.currentPeriod}
        delta={activeStudents.delta}
        trend={activeStudents.trend}
        icon={<SchoolOutlinedIcon fontSize="inherit" />}
      />
      <_OverviewCard
        title="Praktische Prüfungen"
        subtitle="Aktuelle Woche"
        value={practicalExams.thisWeek}
        icon={<VerifiedOutlinedIcon fontSize="inherit" />}
        badge={
          practicalExams.upcoming &&
          practicalExams.upcoming.urgencyLabel &&
          practicalExams.upcoming.countOnNextExamDate > 0
            ? {
                text: `${practicalExams.upcoming?.countOnNextExamDate} ${practicalExams.upcoming?.urgencyLabel}`,
                labelColor: _badgeLabelColor.exams,
              }
            : undefined
        }
        badgeDescription={practicalExams.upcoming?.urgencyLabel ? "anstehend" : undefined}
      />
      <_OverviewCard
        title={`Bestehens\u00ADquote`}
        subtitle="Letzte 30 Tage"
        value={`${practicalPassRate.currentPeriod ?? 0}%`}
        delta={practicalPassRate.delta ?? 0}
        trend={practicalPassRate.trend ?? "stable"}
        icon={<EmojiEventsOutlinedIcon fontSize="inherit" />}
      />

      <_OverviewCard
        title={`Unter\u00ADschriften\u00ADmappe`}
        subtitle="Dokumente"
        value={openSignatures.count}
        icon={<FolderOutlinedIcon fontSize="inherit" />}
        iconBadgeContent={openSignatures.count}
        badge={openSignatures.count > 0 ? { text: "Wichtig", labelColor: _badgeLabelColor.signatures } : undefined}
        badgeDescription={openSignatures.count > 0 ? "In der App" : undefined}
        onClick={grants.has("Document:viewSignatureFolder") ? openDialog : undefined}
      />
      <SignatureFoldersDialog
        {...dialogProps}
        drivingSchoolId={drivingSchoolId}
        onDocumentsLoaded={(count) => {
          queryClient.setQueryData<StatisticsOverviewDto | undefined>(
            ["statisticsOverview", drivingSchoolId],
            (previous) =>
              previous ? { ...previous, openSignatures: { ...previous.openSignatures, count } } : previous,
          );
        }}
      />
    </Box>
  );
}

const overviewCardsContainerStyle = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
  alignItems: "stretch",
  gap: { xs: 1.5, md: 3 },
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  "& > .MuiCard-root": {
    minWidth: 0,
  },
  "@media (min-width: 1280px)": {
    display: "flex",
    gap: "30px",
    "& > .MuiCard-root": {
      flex: "1 1 0",
    },
  },
};

function _OverviewCardSkeleton(props: { title: string; subtitle: string }) {
  const { title, subtitle } = props;

  return (
    <Card
      sx={{
        ...overviewCardStyle,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: { xs: "18px", md: overviewCardStyle.padding },
      }}
    >
      <Skeleton
        variant="circular"
        sx={{
          position: "absolute",
          top: { xs: 18, md: 22 },
          right: { xs: 18, md: 24 },
          width: { xs: 36, md: 44 },
          height: { xs: 36, md: 44 },
        }}
      />
      <Box sx={{ paddingRight: { xs: "42px", md: "53px" }, minWidth: 0 }}>
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <Typography
            aria-hidden
            variant="h6"
            sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.25, visibility: "hidden" }}
          >
            {title}
          </Typography>
          <Skeleton
            variant="text"
            width="65%"
            sx={{ position: "absolute", top: 0, fontSize: { xs: 14, md: 15 }, lineHeight: 1.25 }}
          />
          <Skeleton
            variant="text"
            width="50%"
            sx={{ position: "absolute", top: "1.25em", fontSize: { xs: 14, md: 15 }, lineHeight: 1.25 }}
          />
          <Skeleton
            variant="text"
            width="40%"
            sx={{ position: "absolute", top: "2.5em", fontSize: { xs: 14, md: 15 }, lineHeight: 1.25 }}
          />
        </Box>
        <Box sx={{ position: "relative", overflow: "hidden", mt: "6px" }}>
          <Typography aria-hidden variant="caption" sx={{ display: "block", lineHeight: 1.2, visibility: "hidden" }}>
            {subtitle}
          </Typography>
          <Skeleton variant="text" width="45%" sx={{ position: "absolute", top: 0, fontSize: 12, lineHeight: 1.2 }} />
          <Skeleton
            variant="text"
            width="35%"
            sx={{ position: "absolute", top: "1.2em", fontSize: 12, lineHeight: 1.2 }}
          />
        </Box>
      </Box>
      <Box sx={{ mt: "auto" }}>
        <Skeleton variant="text" width="25%" sx={{ mt: "12px", fontSize: { xs: 24, md: 28 }, lineHeight: 1.334 }} />
        <Box sx={{ mt: "16px", minHeight: 29, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Skeleton variant="rounded" width={64} height={29} />
          <Skeleton variant="text" width="45%" sx={{ fontSize: 12, lineHeight: 1.66 }} />
        </Box>
      </Box>
    </Card>
  );
}

function _OverviewCard(props: {
  title: string;
  subtitle?: string;
  value: number | string;
  icon?: React.ReactNode;
  iconBadgeContent?: number;
  delta?: number;
  trend?: "up" | "down" | "stable";
  badge?: {
    text: string;
    labelColor: DrivingSchoolLabel["color"];
  };
  badgeDescription?: string;
  onClick?: () => void;
}) {
  const { title, subtitle, value, icon, iconBadgeContent, delta, trend, badge, badgeDescription, onClick } = props;
  const primaryColor = useThemeColor("primary");
  const studentLabelColors = useStudentLabelPalette();
  const trendBadge =
    trend && delta !== undefined
      ? trend === "stable"
        ? {
            text: `${delta}%`,
            backgroundColor: "action.selected",
            color: "text.secondary",
          }
        : {
            text: `${trend === "up" ? "+" : ""}${delta}%`,
            backgroundColor: studentLabelColors[_badgeLabelColor[trend]].background,
            color: studentLabelColors[_badgeLabelColor[trend]].text,
          }
      : undefined;
  const resolvedBadge = badge
    ? {
        text: badge.text,
        backgroundColor: studentLabelColors[badge.labelColor].background,
        color: studentLabelColors[badge.labelColor].text,
      }
    : trendBadge;
  const resolvedBadgeDescription = badgeDescription ?? (trendBadge ? "Vorheriger Zeitraum" : undefined);

  return (
    <Card
      component={onClick ? CardActionArea : "div"}
      onClick={onClick}
      aria-label={onClick ? "Unterschriftenmappen öffnen" : undefined}
      sx={{
        ...overviewCardStyle,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: { xs: "18px", md: overviewCardStyle.padding },
        textAlign: "left",
        alignItems: "stretch",
      }}
    >
      {icon && (
        <Badge
          badgeContent={iconBadgeContent}
          invisible={!iconBadgeContent}
          sx={{
            position: "absolute",
            top: { xs: 18, md: 22 },
            right: { xs: 18, md: 24 },
            "& .MuiBadge-badge": {
              top: 6,
              right: 6,
              backgroundColor: "var(--autovio-color-red)",
              color: "common.white",
              fontSize: { xs: 10, md: 11 },
              fontWeight: "bold",
              minWidth: { xs: 16, md: 18 },
              height: { xs: 16, md: 18 },
            },
          }}
        >
          <Box
            sx={{
              width: { xs: 36, md: 44 },
              height: { xs: 36, md: 44 },
              borderRadius: "50%",
              backgroundColor: primaryColor.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: { xs: 20, md: 24 },
              color: primaryColor.contrastText,
            }}
          >
            {icon}
          </Box>
        </Badge>
      )}
      <Box sx={icon ? { paddingRight: { xs: "42px", md: "53px" }, minWidth: 0 } : { minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: 14, md: 15 }, lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", mt: "6px", lineHeight: 1.2, display: "block" }}>
          {subtitle ?? "\u00A0"}
        </Typography>
      </Box>
      <Box sx={{ mt: "auto" }}>
        <Typography variant="h5" sx={{ mt: "12px", fontWeight: 600, fontSize: { xs: 24, md: 28 } }}>
          {value}
        </Typography>
        <Box sx={{ mt: "16px", minHeight: 29, display: "flex", alignItems: "center", minWidth: 0 }}>
          {(resolvedBadge || resolvedBadgeDescription) && (
            <Row
              gap={0.5}
              alignItems="center"
              sx={{ flexWrap: "nowrap", width: "100%", minWidth: 0, overflow: "hidden" }}
            >
              {resolvedBadge && (
                <_Badge
                  text={resolvedBadge.text}
                  backgroundColor={resolvedBadge.backgroundColor}
                  color={resolvedBadge.color}
                />
              )}
              {resolvedBadgeDescription && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: 1.2,
                    minWidth: 0,
                    flex: "1 1 auto",
                  }}
                >
                  {resolvedBadgeDescription}
                </Typography>
              )}
            </Row>
          )}
        </Box>
      </Box>
    </Card>
  );
}

const badgeStyle = {
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  padding: "4px 8px",
  borderRadius: "18px",
};

function _Badge(props: { text: string; backgroundColor: string; color: string }) {
  const { text, backgroundColor, color } = props;
  return (
    <Box sx={{ ...badgeStyle, backgroundColor, color }}>
      <Typography variant="caption">{text}</Typography>
    </Box>
  );
}

const _badgeLabelColor = {
  up: "greenUltraLight",
  down: "redUltraLight",
  signatures: "redUltraLight",
  exams: "orangeUltraLight",
} as const satisfies Record<string, DrivingSchoolLabel["color"]>;
